import React, { useCallback, useMemo } from "react";
import { countryList } from "./country-list";
import { CountryCodeInput } from "./country-code-input";
import { PhoneNumberInput } from "./phone-number-input";
import type { CountryConfig } from "./country-list";
import { formatWithMask, parseDigits } from "../../lib";
import WarningSvg from "@/shared/assets/images/Warning.svg";
import type { FieldError } from "react-hook-form";

const DEFAULT_MASK = "______________";

function findCountryByPhone(
  fullPhone: string,
  list: CountryConfig[]
): CountryConfig | null {
  const digits = fullPhone.replace(/\D/g, "");
  if (!digits.length) return list[0] ?? null;
  const sorted = [...list].sort((a, b) => b.code.length - a.code.length);
  const codeDigits = fullPhone.startsWith("+")
    ? fullPhone.slice(1).replace(/\D/g, "")
    : fullPhone.replace(/\D/g, "");
  for (const c of sorted) {
    const codeOnly = c.code.replace(/\D/g, "");
    if (codeDigits.startsWith(codeOnly)) return c;
  }
  return list[0] ?? null;
}

function getLocalDigits(
  fullPhone: string,
  code: CountryConfig["code"]
): string {
  const full = fullPhone.replace(/\D/g, "");
  const codeNum = code.replace(/\D/g, "");
  if (!full.startsWith(codeNum)) return "";
  return full.slice(codeNum.length);
}

export type PhoneInputProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: FieldError;
  required?: boolean;
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  label,
  value: fullValue,
  onChange,
  error,
  required = true,
}) => {
  const country = useMemo(
    () => findCountryByPhone(fullValue, countryList),
    [fullValue]
  );
  const code = country?.code ?? "+7";
  const mask = country?.mask ?? DEFAULT_MASK;
  const localDigits = useMemo(
    () => getLocalDigits(fullValue, code),
    [fullValue, code]
  );
  const localFormatted = useMemo(
    () => formatWithMask(localDigits, mask),
    [localDigits, mask]
  );

  const handleCodeChange = useCallback(
    (newCode: CountryConfig["code"]) => {
      onChange(newCode + localDigits);
    },
    [localDigits, onChange]
  );

  const handlePhoneChange = useCallback(
    (formattedLocal: string) => {
      const digits = parseDigits(formattedLocal);
      onChange(code + digits);
    },
    [code, onChange]
  );

  return (
    <div
      className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${error ? "border-color-danger" : ""}`}
    >
      <div className="flex justify-between w-full items-center">
        <label
          htmlFor={id}
          className="font-semibold uppercase text-base text-[#828282]"
        >
          {label}
          {required && " *"}
        </label>
        {error && (
          <span className="text-base flex items-center text-color-danger">
            <WarningSvg width={20} height={20} className="me-1" />
            {error.message}
          </span>
        )}
      </div>
      <div className="flex gap-2 items-stretch w-full">
        <div className="flex flex-col min-w-0 w-[120px] shrink-0">
          <CountryCodeInput
            countryList={countryList}
            value={code}
            onChange={handleCodeChange}
            error={!!error}
          />
        </div>
        <div className="flex-1 min-w-0">
          <PhoneNumberInput
            id={id}
            mask={mask}
            value={localFormatted}
            onChange={handlePhoneChange}
            error={!!error}
          />
        </div>
      </div>
    </div>
  );
};
